import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import type { AdminFeatureImage } from '../../types/admin';
import { adminFeatureApi } from '../../api/adminApi';
import { getApiErrorMessage, resolveImageUrl } from '../../api/client';
import { notify } from '../../components/Notifier';
import { Can } from '../../auth/Can';
import { ImageUploader } from '../../components/ImageUploader';

/** Chỉ cần id + name để hiển thị — dùng được cho cả AdminFeature lẫn FeatureWithStatus. */
interface FeatureRef {
  id: string;
  name: string;
}

interface Props {
  /** Tính năng cần xem ảnh; null = đóng dialog. */
  feature: FeatureRef | null;
  onClose: () => void;
  /** Gọi sau khi upload/xoá ảnh thành công, để nơi gọi âm thầm cập nhật lại chỗ hiển thị ảnh khác (nếu có). */
  onImagesChanged?: () => void;
}

/**
 * Dialog quản lý hình ảnh của một tính năng (xem / tải lên / xoá mềm). Tách từ
 * FeatureManagement để dùng chung cho khu quản trị và màn nghiệm thu. Các thao
 * tác đều theo phân quyền ảnh: hiển thị cần FEATURE_IMAGE_VIEW (phía gọi gate
 * nút mở), tải lên cần FEATURE_IMAGE_CREATE, xoá cần FEATURE_IMAGE_DELETE.
 */
export function FeatureImagesDialog({ feature, onClose, onImagesChanged }: Props): JSX.Element {
  const [images, setImages] = useState<AdminFeatureImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => {
    if (!feature) {
      setImages([]);
      return;
    }
    let active = true;
    setLoading(true);
    adminFeatureApi
      .listImages(feature.id)
      .then((list) => {
        if (active) setImages(list);
      })
      .catch((err) => {
        if (active) {
          notify(getApiErrorMessage(err, 'Không thể tải danh sách hình ảnh'), 'error');
          setImages([]);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [feature]);

  const handleUpload = async (files: File[]) => {
    if (!feature) return;
    try {
      const uploaded = await adminFeatureApi.uploadImages(feature.id, files);
      setImages((prev) => [...prev, ...uploaded]);
      notify(`Đã tải lên ${uploaded.length} ảnh`, 'success');
      onImagesChanged?.();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải lên hình ảnh'), 'error');
    }
  };

  const handleRemove = async (imageId: string) => {
    setRemovingId(imageId);
    try {
      await adminFeatureApi.removeImage(imageId);
      notify('Đã xóa mềm hình ảnh', 'success');
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      onImagesChanged?.();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xóa hình ảnh'), 'error');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Dialog open={feature !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Hình ảnh tính năng: {feature?.name}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {images.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Chưa có hình ảnh nào.
              </Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 3 }}>
                {images.map((img) => (
                  <Box
                    key={img.id}
                    sx={{
                      position: 'relative',
                      width: 140,
                      height: 140,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      component="img"
                      src={resolveImageUrl(img)}
                      alt={img.caption ?? 'Hình ảnh tính năng'}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <Can permission="FEATURE_IMAGE_DELETE">
                      <IconButton
                        size="small"
                        onClick={() => void handleRemove(img.id)}
                        disabled={removingId === img.id}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          bgcolor: 'rgba(0,0,0,0.55)',
                          color: '#fff',
                          p: 0.25,
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                        }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Can>
                  </Box>
                ))}
              </Stack>
            )}
            <Can permission="FEATURE_IMAGE_CREATE">
              <ImageUploader onUpload={handleUpload} multiple />
            </Can>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
      </DialogActions>
    </Dialog>
  );
}
